import React, { useState, useRef, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import { Bell, BellOff, MessageSquare, VolumeX } from "lucide-react";

import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
	pedirPermissaoDeAviso,
	podeAvisar,
} from "../../helpers/avisoDoNavegador";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import useSomDeNotificacao from "../../hooks/useSomDeNotificacao";
import { AuthContext } from "../../context/Auth/AuthContext";

const NotificationsPopOver = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const [, setDesktopNotifications] = useState([]);

	// Este componente não é só o balão: é ele que ouve `appMessage`, toca o
	// aviso sonoro e dispara a notificação do navegador. Por isso o ícone é
	// escondido AQUI, e não em quem monta o componente: montado
	// condicionalmente, esconder o balão desligava o som e o aviso do sistema
	// junto — sem ninguém ter pedido isso.
	//
	// O balão está oculto por decisão de produto (15/09/2026): o ícone saiu do
	// cabeçalho e a configuração saiu das Configurações. Para reativar, volte
	// esta linha para `useMostrarNaoLidas()` e o card "Balão de não lidas" em
	// pages/Settings/index.jsx — a chave `mostrarNaoLidas` continua no banco.
	const mostrarBalao = false;

	const { tickets, tentarDeNovo } = useTickets({
		// Só conversa viva: sem o recorte, atendimento encerrado com mensagem
		// não lida continuava contando no sino para sempre.
		status: "open,pending",
		withUnreadMessages: "true",
		needsCount: false,
	});
	// Som e volume vêm da configuração da empresa e do ajuste de quem ouve.
	const { tocar } = useSomDeNotificacao();
	const soundAlertRef = useRef();

	const navigateRef = useRef(navigate);

	useEffect(() => {
		soundAlertRef.current = tocar;
		// O pedido de permissão NÃO mora aqui. Navegador ignora — e o Firefox
		// conta como negado — pedido que nasce de um efeito, sem gesto de quem
		// está usando. Ele passou para dentro do clique, no botão da faixa
		// abaixo, que é onde o gesto existe.
	}, [tocar]);

	// `default` = ninguém decidiu ainda; `denied` = o navegador bloqueou e não
	// há como pedir de novo por código, só pelo cadeado da barra de endereço.
	// Três estados, não dois: "default" (ninguém decidiu), "denied" (o
	// navegador bloqueou — só o cadeado libera) e "sem-suporte", que NÃO é
	// bloqueio: mandar procurar cadeado onde não há permissão nenhuma seria
	// mandar para o lugar errado.
	const lerPermissao = () =>
		podeAvisar() ? Notification.permission : "sem-suporte";

	const [permissao, setPermissao] = useState(lerPermissao);

	// O som só toca depois que a pessoa interage com a página — política do
	// navegador. Sem sinal nenhum, a impressão é de aviso quebrado.
	const [somBloqueado, setSomBloqueado] = useState(false);
	const jaInteragiu = useRef(false);

	useEffect(() => {
		const marcar = () => {
			jaInteragiu.current = true;
			setSomBloqueado(false);
		};

		window.addEventListener("pointerdown", marcar, { once: true });
		window.addEventListener("keydown", marcar, { once: true });

		return () => {
			window.removeEventListener("pointerdown", marcar);
			window.removeEventListener("keydown", marcar);
		};
	}, []);

	// Liberar pelo cadeado com a página aberta não avisa ninguém: sem
	// ressincronizar, a faixa e o ponto ficam mentindo até um F5.
	useEffect(() => {
		const sincronizar = () => setPermissao(lerPermissao());

		window.addEventListener("focus", sincronizar);
		return () => window.removeEventListener("focus", sincronizar);
	}, []);

	const ativarAvisos = async () => {
		setPermissao(await pedirPermissaoDeAviso());
	};

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => socket.emit("joinNotification"));

		// Conexão que caiu e ninguém viu: o vigia do backend avisa a sala dos
		// administradores. A bolinha no menu já dizia o estado, mas só para
		// quem estivesse olhando — e atendimento parado não espera alguém
		// olhar. O evento só chega a quem entra na sala de administrador.
		socket.on("channelAlert", (data) => {
			if (!data?.title) return;

			const aviso = `${data.title}. ${data.body || ""}`.trim();

			if (data.action === "up") {
				toast.success(aviso);
				return;
			}

			// Não some sozinho: é o tipo de aviso que precisa ser lido e
			// resolvido, não visto de canto de olho por três segundos.
			toast.error(aviso, { autoClose: false });
		});

		// Escalada de SLA estourada num atendimento que ninguém respondeu. Só
		// chega a quem está na sala de administradores (o backend restringe);
		// aqui é só mostrar. Não some sozinho: é para ser lido e resolvido.
		socket.on("sla-escalation", (data) => {
			if (!data?.ticketId) return;

			toast.warning(
				`${data.nivel || "SLA estourado"}: ${data.contato} está há ${
					data.minutos
				}min sem resposta no departamento ${data.fila}.`,
				{
					autoClose: false,
					onClick: () => navigate(`/tickets/${data.ticketId}`),
				}
			);
		});

		socket.on("ticket", (data) => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex(
						(t) => t.id === data.ticketId
					);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications((prevState) => {
					const notfiticationIndex = prevState.findIndex(
						(n) => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		});

		socket.on("appMessage", (data) => {
			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex(
						(t) => t.id === data.ticket.id
					);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});

		// Conexão que voltou: o que foi lido ou chegou no intervalo não passou
		// por aqui, e o sino ficaria com a conta de antes da queda.
		socket.on("resync", tentarDeNovo);

		return () => {
			socket.disconnect();
		};
	}, [user, tentarDeNovo]);

	const handleNotifications = (data) => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		// O aviso do sistema é a parte OPCIONAL disto aqui: sem a API (Android,
		// navegador antigo) o construtor lança, e o que vinha depois — o sinal
		// de som bloqueado e o próprio som — morria junto. O que importa para
		// quem atende é o som e a contagem; o aviso do sistema é bônus.
		try {
			if (podeAvisar() && Notification.permission === "granted") {
				const notification = new Notification(
					`${i18n.t("tickets.notification.message")} ${contact.name}`,
					options
				);

				notification.onclick = (e) => {
					e.preventDefault();
					window.focus();
					navigateRef.current(`/tickets/${ticket.id}`);
				};

				setDesktopNotifications((prevState) => {
					const notfiticationIndex = prevState.findIndex(
						(n) => n.tag === notification.tag
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex] = notification;
						return [...prevState];
					}
					return [notification, ...prevState];
				});
			}
		} catch {
			// Android lança no construtor: lá o caminho é o service worker, que
			// é débito registrado. Não pode custar o som.
		}

		// Tocado antes da primeira interação, o navegador engole o som. Em vez
		// de avisar sempre sobre a política, o sinal aparece só quando doeu — e
		// some no primeiro clique. Sem repetir o som perdido: barulho atrasado
		// do nada assusta mais do que informa, e a bolha do sino já diz que
		// chegou coisa.
		// `userActivation.hasBeenActive` é do NAVEGADOR: sobrevive a este
		// componente remontar (ele vive no cabeçalho e há uma segunda instância
		// no móvel). O ref fica de reserva para quem não tem a API.
		const jaHouveGesto =
			navigator.userActivation?.hasBeenActive ?? jaInteragiu.current;

		if (!jaHouveGesto) setSomBloqueado(true);

		soundAlertRef.current();
	};

	// Sem ícone, mas vivo: os efeitos acima já estão registrados — hook roda
	// mesmo quando a renderização não devolve nada — então o som e o aviso do
	// navegador continuam chegando.
	if (!mostrarBalao) return null;

	return (
		<Popover
			open={isOpen}
			onOpenChange={(aberto) => {
				if (aberto) setPermissao(lerPermissao());
				setIsOpen(aberto);
			}}
		>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative"
					aria-label="Open Notifications"
				>
					<MessageSquare className="h-5 w-5" />
					{notifications.length > 0 && (
						<span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold tabular-nums text-destructive-foreground">
							{notifications.length}
						</span>
					)}
					{/* Quem nunca abre o sino também precisa saber que os avisos do
					    sistema estão desligados — ponto discreto, não alarme. */}
					{permissao !== "granted" && permissao !== "sem-suporte" && (
						<span
							title="Avisos do sistema desligados. Abra aqui para ativar."
							className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-amber-500"
						/>
					)}
					{somBloqueado && (
						<span
							title="O som de aviso toca depois do primeiro clique na página"
							className="absolute -bottom-0.5 left-0.5"
						>
							<VolumeX className="h-3 w-3 text-muted-foreground" />
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-0">
				<div className="border-b px-3 py-2">
					<p className="text-sm font-medium">Não lidas</p>
				</div>

				{/* O clique no sino É o gesto que autoriza pedir a permissão: sem
				    modal atravessado e sem pedir no primeiro segundo de uso. */}
				{permissao === "default" && (
					<div className="mx-3 mt-2 flex items-center gap-2 rounded-md bg-muted px-2 py-1.5 text-xs">
						<Bell className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
						<span className="flex-1">Avisos do sistema desligados.</span>
						<button
							type="button"
							onClick={ativarAvisos}
							className="font-medium text-primary underline underline-offset-2"
						>
							Ativar
						</button>
					</div>
				)}

				{permissao === "denied" && (
					<div className="mx-3 mt-2 flex items-start gap-2 rounded-md bg-muted px-2 py-1.5 text-xs">
						<BellOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
						{/* Sem botão: negado não se pede de novo por código. O texto é
						    o caminho. */}
						<span>
							O navegador bloqueou os avisos deste site. Libere no cadeado da
							barra de endereço.
						</span>
					</div>
				)}
				<div className="max-h-[350px] overflow-y-auto">
					{notifications.length === 0 ? (
						<p className="px-3 py-8 text-center text-xs text-muted-foreground">
							{i18n.t("notifications.noTickets")}
						</p>
					) : (
						notifications.map((ticket) => (
							// Clicar num item navega e fecha o popover.
							<div key={ticket.id} onClick={() => setIsOpen(false)}>
								<TicketListItem ticket={ticket} />
							</div>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default NotificationsPopOver;
